import { createFederationStorage } from "@constructor/federation-common/base";
function useFragmentComponent({
  loadEntry,
  onDestroy,
  injector,
  getDefine,
  processException
}) {
  const context = { inject: injector.inject };
  const properties = createFederationStorage();
  const subfragmentsManager = injector.__internal.inject("subfragmentsFabric");
  const rootHandlers = injector.__internal.inject("rootHandlers");
  const componentInfo = injector.inject("componentInfo");
  const subfragments = subfragmentsManager.createSubfragmentsService({ injector });
  const componentHandlers = rootHandlers.createComponentHandlers(componentInfo);
  let entryComponent;
  let status = "new";
  let creatingPromise;
  let rootDataService;
  return {
    properties,
    create,
    mount,
    unmount,
    destroy,
    getStatus
  };
  function getStatus() {
    return status;
  }
  async function create(options) {
    if (status !== "new") {
      throw new Error(
        `You try to run create() for component which is in ${status} state. To create another component instance you should call createComponent() in entry`
      );
    }
    await loadEntry();
    if (status === "destroyed") {
      return;
    }
    injector.provide("handlers", componentHandlers.publicApi);
    injector.provide("properties", properties);
    injector.provide("logger", { processException });
    injector.provide("subfragments", subfragments);
    provideFederationRouter(options == null ? void 0 : options.router);
    provideRootData();
    status = "creating";
    creatingPromise = Promise.resolve().then(() => getDefine().definition.cb(context));
    entryComponent = await creatingPromise;
    creatingPromise = void 0;
    if (status === "destroyed") {
      return;
    }
    status = "created";
  }
  function provideRootData() {
    const rootDataFabric = injector.__internal.inject("rootDataFabric", {
      defaultValue: void 0
    });
    if (!rootDataFabric) {
      return;
    }
    rootDataService = rootDataFabric.createRootDataService();
    injector.provide("rootData", rootDataService);
  }
  function provideFederationRouter(options) {
    const existingRouter = injector.inject("router", { defaultValue: void 0 });
    if (existingRouter) {
      if (options) {
        throw new Error(
          `Passing "router" option (${JSON.stringify(options)}) for subfragments is not allowed`
        );
      }
      return;
    }
    const routerFabric = injector.__internal.inject("routerFabric", { defaultValue: void 0 });
    if (routerFabric) {
      injector.provide("router", routerFabric.createFederationRouter(options));
    } else {
      if (options) {
        throw new Error('To use router you should call "setRouterFabric()" in the federation root');
      }
    }
  }
  function mount(el) {
    checkStatus("mounted", ["created", "unmounted"]);
    try {
      entryComponent.mount(el);
      status = "mounted";
    } catch (error) {
      processException(error);
      status = "error";
    }
  }
  function unmount() {
    var _a;
    checkStatus("unmounted", ["mounted", "error"]);
    if (status === "error") {
      return;
    }
    try {
      (_a = entryComponent == null ? void 0 : entryComponent.unmount) == null ? void 0 : _a.call(entryComponent);
      status = "unmounted";
    } catch (error) {
      processException(error);
      status = "error";
    }
  }
  function destroy() {
    var _a, _b;
    checkStatus("destroyed", ["new", "created", "creating", "unmounted", "error"]);
    if (status === "creating") {
      creatingPromise == null ? void 0 : creatingPromise.then((component) => {
        var _a2;
        return (_a2 = component.destroy) == null ? void 0 : _a2.call(component);
      });
    } else {
      (_a = entryComponent == null ? void 0 : entryComponent.destroy) == null ? void 0 : _a.call(entryComponent);
      entryComponent = void 0;
    }
    status = "destroyed";
    componentHandlers.destroy();
    (_b = rootDataService == null ? void 0 : rootDataService.destroy) == null ? void 0 : _b.call(rootDataService);
    rootDataService = void 0;
    onDestroy();
  }
  function checkStatus(targetStatus, requiredStatuses) {
    if (!requiredStatuses.includes(status)) {
      throw new Error(
        `Component in ${status} state can't be ${targetStatus}. ${requiredStatuses.join(" or ")} should be called before`
      );
    }
  }
}
export {
  useFragmentComponent as u
};
//# sourceMappingURL=component.js.map
